import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { ErrorComponent } from '@/ui-legacy/ErrorComponent'
import { FormComponentLoading } from '@/catalog-apps/components/component-library/FormComponent'
import { isProjectLeader } from '@/util/roles'

// Shadcn components
import { Button } from '@/components/shadcn/button'
import { Form } from '@/components/shadcn/form'
import { MultiSelectComboboxField, SelectOption } from '@/components/form-components/MultiSelectCombobox'
import { IconDeleteOutline } from '@icons/IconDeleteOutline'
import { Badge } from '@/components/shadcn/badge'
import { useCurrentOrganisation } from '../useCurrentOrganisation'
import { Avatar, AvatarFallback } from '@/components/shadcn/avatar'
import { getInitials } from '@/lib/utils'

// Form validation schema
const formSchema = z.object({
  selectedLeaderIds: z.array(z.number()).min(1, 'Please select at least one leader to add'),
})

export type MaintainProjectLeadersForm = z.infer<typeof formSchema>

type MessagesProjectsOrganisationProject = RestSchemas['Messages.Projects.OrganisationProject']
type MessagesSubscriptionsOrganisationUser = RestSchemas['Messages.Subscriptions.OrganisationUser']

type MaintainProjectLeadersFormProps = {
  onSubmit?: () => void
  sheetTrigger?: React.ReactNode
  sheetTriggerTooltip?: string
  sheetTitle?: string
  sheetDescription?: string
  openInSheet?: boolean
  organisationId?: number
  selectedProject: MessagesProjectsOrganisationProject
}

export const MaintainProjectLeadersForm = (props: MaintainProjectLeadersFormProps) => {
  const [sheetOpen, setSheetOpen] = useState(false)

  return props.sheetTrigger || props.openInSheet ? (
    <SheetWithFormComponent
      title={props.sheetTitle}
      description={props.sheetDescription}
      trigger={props.sheetTrigger}
      triggerTooltip={props.sheetTriggerTooltip}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <FormComponent {...props} />
    </SheetWithFormComponent>
  ) : (
    <FormComponent {...props} />
  )
}

const FormComponent = (props: MaintainProjectLeadersFormProps) => {
  if (props.organisationId != null && Number.isFinite(props.organisationId)) {
    return <FormComponentWithOrgId {...props} orgId={props.organisationId} />
  }

  return <FormComponentFromRoute {...props} />
}

const FormComponentFromRoute = (props: MaintainProjectLeadersFormProps) => {
  const { id: orgId } = useCurrentOrganisation()
  return <FormComponentWithOrgId {...props} orgId={orgId} />
}

const FormComponentWithOrgId = (props: MaintainProjectLeadersFormProps & { orgId: number }) => {
  const { orgId } = props

  const {
    getSubscriptionsApiListOrganisationUsers,
    putProjectsApiAddProjectLeader,
    deleteProjectsApiRemoveProjectLeader,
    getProjectsApiListOrganisationProjects,
    getProjectsApiGetUserLinkedProjects,
  } = useRestClient()

  const invalidateQueries = () => {
    getProjectsApiListOrganisationProjects.invalidateQueries({ parameters: { query: { orgId } } })
    getProjectsApiGetUserLinkedProjects.invalidateQueries({})
  }

  const {
    data: allOrgUsers,
    isPending: allOrgUsersLoading,
    isError: allOrgUsersError,
  } = getSubscriptionsApiListOrganisationUsers.useQuery(
    {
      query: {
        orgId,
      },
    },
    {
      select: (data) => data.filter((user) => isProjectLeader(user.roles) && user.active),
      enabled: !!orgId,
    }
  )

  const { mutateAsync: addProjectLeader } = putProjectsApiAddProjectLeader.useMutation(undefined, {
    onSuccess: () => {
      invalidateQueries()
      toast.success('Leaders successfully added')
      props.onSubmit?.()
    },
    onError: () => {
      toast.error('Error adding leaders')
    },
  })

  const { mutate: deleteProjectLeader } = deleteProjectsApiRemoveProjectLeader.useMutation(undefined, {
    onSuccess: () => {
      invalidateQueries()
      toast.success('Leader successfully removed')
    },
    onError: () => {
      toast.error('Error removing leader')
    },
  })

  const defaultValues = useMemo(() => {
    return {
      selectedLeaderIds: [] as number[],
    }
  }, [])

  const form = useForm<MaintainProjectLeadersForm>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  // Helper function to get user display name
  const getUserDisplayName = (user: MessagesSubscriptionsOrganisationUser): string => {
    return user.givenName || user.familyName
      ? `${user.givenName ?? ''} ${user.familyName ?? ''}`.trim()
      : 'Unknown User'
  }

  // Create leader options with disambiguation for duplicate names
  const leaderOptions: SelectOption<number>[] = useMemo(() => {
    if (!allOrgUsers) return []

    // Sort users by family name
    const sortedUsers = [...allOrgUsers].sort((a, b) => {
      const aName = a.familyName || ''
      const bName = b.familyName || ''
      return aName.localeCompare(bName)
    })

    // Count name occurrences for disambiguation
    const nameMap: Record<string, number> = {}
    sortedUsers.forEach((user) => {
      const displayName = getUserDisplayName(user)
      nameMap[displayName] = (nameMap[displayName] || 0) + 1
    })

    // Create options with disambiguation when needed
    return sortedUsers
      .filter((user) => !props.selectedProject.leaders.some((leader) => leader.userId === user.id))
      .map((user) => {
        const baseName = getUserDisplayName(user)
        const label = nameMap[baseName] > 1 ? `${baseName} (ID: ${user.id})` : baseName

        return {
          value: user.id,
          label,
        }
      })
  }, [allOrgUsers, props.selectedProject.leaders])

  const handleSubmit = async (data: MaintainProjectLeadersForm) => {
    const promises = data.selectedLeaderIds.map((leaderId) =>
      addProjectLeader({
        query: { adminId: leaderId, projectId: props.selectedProject.projectId },
      })
    )
    await Promise.all(promises)
    form.reset() // Reset form after submission
  }

  const handleDeleteLeader = (leaderId: number) => {
    deleteProjectLeader({
      query: { adminId: leaderId, projectId: props.selectedProject.projectId },
    })
  }

  if (allOrgUsersLoading) return <FormComponentLoading />
  if (allOrgUsersError) return <ErrorComponent />

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <div className="flex h-full flex-col">
            <div className="space-y-6">
              <MultiSelectComboboxField
                control={form.control}
                name="selectedLeaderIds"
                label="Add Project Leaders"
                options={leaderOptions}
                placeholder="Select project leaders to add..."
                searchPlaceholder="Search project leaders..."
                emptyMessage="No available project leaders found."
              />
            </div>
            <div className="form-action-container">
              <Button type="submit" disabled={leaderOptions.length === 0}>
                Add Selected Leaders
              </Button>
            </div>
          </div>
        </form>
      </Form>
      {props.selectedProject.leaders && props.selectedProject.leaders.length > 0 && (
        <div className="space-y-4">
          <div className="text-sm font-medium">Current Project Leaders:</div>
          <div className="space-y-2">
            {props.selectedProject.leaders.map((leader) => (
              <ProjectLeaderCard
                key={leader.userId}
                leader={leader}
                onDelete={() => handleDeleteLeader(leader.userId)}
              />
            ))}
          </div>
        </div>
      )}
    </>
  )
}

// Project leader card component
const ProjectLeaderCard = ({
  leader,
  onDelete,
}: {
  leader: RestSchemas['Messages.Projects.OrganisationProjectUser']
  onDelete: () => void
}) => {
  return (
    <div className="flex items-center justify-between rounded-lg border bg-background p-3">
      <div className="flex items-center gap-5">
        <Avatar className="ring-2 ring-background">
          <AvatarFallback className="bg-accent text-sm text-accent-foreground">
            {getInitials(leader.name)}
          </AvatarFallback>
        </Avatar>
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium">{leader.name}</span>
          <Badge variant="outline">{leader.userId}</Badge>
        </div>
      </div>
      <Button variant="ghost" size="icon" onClick={onDelete}>
        <IconDeleteOutline />
      </Button>
    </div>
  )
}
